import axios from 'axios';

/**
 * Reference-data cache with promise-dedupe.
 *
 * Problem it solves:
 *   Every screen that has a branch selector, an SE dropdown, or an
 *   installment-plan picker re-fetches the same list on mount. Tab-switching
 *   the app produces the same GET /api/branches / /api/users response
 *   dozens of times a session.
 *
 * How it works:
 *   • Keeps a small TTL cache keyed by URL.
 *   • De-duplicates in-flight requests — if two components request the
 *     same URL at the same time, both get the same promise.
 *   • `bust(prefix)` clears matching entries after writes (call it from any
 *     component that mutates the underlying data).
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Data cache: url -> { at, value }
const CACHE = new Map();
// In-flight cache: url -> promise
const INFLIGHT = new Map();

// Per-URL TTL (ms). Reference lists change rarely so 5-min defaults are safe.
const TTL = {
  '/api/branches':          300_000,   // 5 min
  '/api/users/mini':        180_000,   // 3 min — new users trickle in slowly
  '/api/installment-plans': 300_000,
  '/api/settings/company':  600_000,
  __default__:               60_000,   // 1 min for everything else
};

function ttlFor(url) {
  for (const key of Object.keys(TTL)) if (url.startsWith(key)) return TTL[key];
  return TTL.__default__;
}

export async function refFetch(pathOrUrl, { force = false, config = {} } = {}) {
  const url = pathOrUrl.startsWith('http') ? pathOrUrl : `${API_URL}${pathOrUrl}`;
  const now = Date.now();
  if (!force) {
    const hit = CACHE.get(url);
    if (hit && (now - hit.at) < ttlFor(pathOrUrl)) return hit.value;
    const flying = INFLIGHT.get(url);
    if (flying) return flying;
  }
  const p = axios.get(url, { withCredentials: true, ...config })
    .then(res => {
      CACHE.set(url, { at: Date.now(), value: res.data });
      INFLIGHT.delete(url);
      return res.data;
    })
    .catch(err => {
      INFLIGHT.delete(url);
      throw err;
    });
  INFLIGHT.set(url, p);
  return p;
}

export function bustRefCache(prefix = '') {
  if (!prefix) { CACHE.clear(); INFLIGHT.clear(); return; }
  for (const k of Array.from(CACHE.keys())) if (k.includes(prefix)) CACHE.delete(k);
  for (const k of Array.from(INFLIGHT.keys())) if (k.includes(prefix)) INFLIGHT.delete(k);
}

// React hook wrapper for convenience.
import { useEffect, useState } from 'react';
export function useRefData(path, { skip = false, config = {} } = {}) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(!skip);
  useEffect(() => {
    if (skip) return;
    let cancel = false;
    setLoading(true);
    refFetch(path, { config })
      .then((d) => { if (!cancel) { setData(d); setError(null); } })
      .catch((e) => { if (!cancel) setError(e); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [path, skip]);   // eslint-disable-line react-hooks/exhaustive-deps
  return { data, error, loading, refetch: () => refFetch(path, { force: true, config }) };
}
